if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.deferredLoaded = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.totalMediaCount = parseInt(this.dataset.totalMediaCount) || 0;
        this.initialMediaLimit = parseInt(this.dataset.initialMediaLimit) || this.totalMediaCount;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }

        this.initialized = true;
        this.scheduleDeferredLoad();

        if (this.dataset.filterImagesEnabled === 'true') {
          this.initAddonImageSelect();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 0,
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.syncThumbsPosition();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      scheduleDeferredLoad() {
        if (this.deferredLoaded || !this.querySelector('[id^="DeferredSlides"]')) {
          this.deferredLoaded = true;
          return;
        }

        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.loadDeferredSlides(), { timeout: 1500 });
        } else {
          setTimeout(() => this.loadDeferredSlides(), 200);
        }
      }

      ensureDeferredLoaded() {
        if (!this.deferredLoaded) {
          this.loadDeferredSlides();
        }
      }

      loadDeferredSlides() {
        if (this.deferredLoaded) return;

        const deferredSlidesTemplate = this.querySelector('[id^="DeferredSlides"]');
        if (!deferredSlidesTemplate) {
          this.deferredLoaded = true;
          return;
        }

        const deferredSlides = [...deferredSlidesTemplate.content.querySelectorAll('.product__media-item')];
        const deferredThumbsTemplate = this.querySelector('[id^="DeferredThumbnails"]');

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          const swiperWrapper = this.sliderInstance.slider.wrapperEl;
          deferredSlides.forEach((slide) => swiperWrapper.appendChild(slide.cloneNode(true)));

          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider && deferredThumbsTemplate) {
            const thumbsWrapper = this.thumbsInstance.slider.wrapperEl;
            const thumbs = [...deferredThumbsTemplate.content.querySelectorAll('.product__thumbs-item')];
            thumbs.forEach((thumb) => thumbsWrapper.appendChild(thumb.cloneNode(true)));
            this.thumbsInstance.slider.update();
          }

          this.sliderInstance.slider.update();
          this.syncThumbsPosition();
        } else {
          this.elements.mediaList.appendChild(deferredSlidesTemplate.content);
        }

        this.elements.mediaItems = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];

        if (this.enableImageZoom) {
          this.initImageZoom(this.elements.mediaItems);
        }

        deferredSlidesTemplate.remove();
        deferredThumbsTemplate?.remove();
        this.deferredLoaded = true;
      }

      syncThumbsPosition() {
        if (typeof this.thumbsInstance !== 'object' || !this.thumbsInstance.slider) return;
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return;

        const thumbsSlider = this.thumbsInstance.slider;
        const mainSlider = this.sliderInstance.slider;
        if (thumbsSlider.destroyed || mainSlider.destroyed) return;

        const realIndex = mainSlider.realIndex;

        // Manually update active class since slideToLoop uses runCallbacks: false,
        // which prevents the Thumbs module from updating the active state.
        thumbsSlider.slides?.forEach((slide, i) => {
          slide.classList.toggle('swiper-slide-thumb-active', i === realIndex);
        });

        thumbsSlider.slideTo(realIndex, 0);
      }

      destroyLightbox() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      // Build a PhotoSwipe dataSource from a list of media items. Extracted
      // so the lightbox list can be rebuilt at open time from the slides
      // actually present in the gallery (the color/addon filter and the
      // Rubik app both remove slides from the DOM after init).
      buildLightboxDataSource(items = []) {
        const dataSource = [];
        items.forEach((media) => {
          switch (media.dataset.mediaType) {
            case 'model':
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: `<div class="pswp__item--${media.dataset.mediaType}">${
                  media.querySelector('product-model').outerHTML
                }</div>`,
                mediaType: media.dataset.mediaType,
              });
              break;
            case 'video':
            case 'external_video':
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: `<div class="pswp__item--${media.dataset.mediaType}">${
                  media.querySelector('video-element').outerHTML
                }</div>`,
                mediaType: media.dataset.mediaType,
              });
              break;
            case 'image':
              dataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                alt: media.dataset.alt || '',
                mediaType: media.dataset.mediaType,
              });
              break;
          }
        });
        return dataSource;
      }

      // The media items currently live in the gallery, in DOM order.
      // Slides removed by the color/addon filter (or by the Rubik app) are
      // no longer in the DOM, so this naturally reflects what the person
      // actually sees.
      getLiveMediaItems() {
        return [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')].filter(
          (el) => el.style.display !== 'none'
        );
      }

      initImageZoom(items = []) {
        this.destroyLightbox();
        let dataSource = this.buildLightboxDataSource(items);

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              // The lightbox was built at init with EVERY media item, but the
              // color/addon filter (and the Rubik app) remove slides from the
              // DOM afterwards - so fullscreen used to show all photos of all
              // colors. Rebuild the dataSource from the slides actually in
              // the gallery right now, and open at the clicked slide's
              // position within that live list.
              const liveItems = this.getLiveMediaItems();
              this.lightbox.options.dataSource = this.buildLightboxDataSource(liveItems);

              // `media` is whichever element carries .js-photoswipe--zoom,
              // which is not necessarily the .product__media-item that
              // liveItems contains (the zoom class can sit on an inner
              // wrapper). A raw indexOf(media) then always missed, fell
              // through the < 0 fallback, and opened fullscreen on the
              // FIRST image no matter which one was tapped. Resolve the
              // owning media item first, and fall back to matching by
              // media id before ever defaulting to 0.
              const mediaItem = media.closest('.product__media-item') || media;
              let index = liveItems.indexOf(mediaItem);
              if (index < 0) {
                const mediaId = mediaItem.dataset.mediaId || media.dataset.mediaId;
                if (mediaId != null && mediaId !== '') {
                  index = liveItems.findIndex((el) => String(el.dataset.mediaId) === String(mediaId));
                }
              }
              if (index < 0) index = 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        try {
          // Always make sure every slide exists before we try to reach one.
          // The deferred slides load on requestIdleCallback, so a fast swatch
          // click can land here while slides past the initial limit are still
          // only markup inside a <template>.
          this.ensureDeferredLoaded();

          const mediaId = variant.featured_media.id;

          if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
            const slider = this.sliderInstance.slider;
            if (slider.destroyed) return;

            // Resolve the slide by media id rather than trusting
            // featured_media.position. Position counts every media on the
            // product; the slider index only matches it when all media are
            // rendered, in order, with no duplicates. When they diverge we
            // used to slide to an index that does not exist, which collapses
            // autoHeight and leaves the gallery blank until a page refresh.
            const slides = [...slider.slides];
            let targetIndex = slides.findIndex(
              (slide) => slide.dataset.mediaId == mediaId && !slide.classList.contains('swiper-slide-duplicate')
            );

            // Fall back to the position-based index, clamped so it can never
            // point past the end of the slider.
            if (targetIndex < 0) {
              const position = parseInt(variant.featured_media.position) || 1;
              targetIndex = Math.min(Math.max(position - 1, 0), slides.length - 1);
            }

            if (targetIndex < 0) return;

            slider.slideToLoop(targetIndex, 0, false);
            slider.update();
            this.syncThumbsPosition();
          } else {
            this.sortMediaItems(variant);
          }
        } catch (e) {
          // Never let a gallery problem stop the rest of the variant update
          // (price, badges, buy buttons) from rendering.
          console.warn('[media-gallery] setActiveMedia failed:', e);
        }
      }

      sortMediaItems(variant) {
        this.ensureDeferredLoaded();

        // Re-query in case the gallery markup was swapped out since init.
        if (!this.elements.mediaList || !this.elements.mediaList.isConnected) {
          this.elements.mediaList = this.querySelector(this.selectors.mediaList);
        }
        if (!this.elements.mediaList) return;

        let newMedias = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (!newMedias.length) return;
        this.elements.mediaItems = newMedias;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        if (this.enableImageZoom) {
          this.initImageZoom(newMedias);
        }

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: FoxTheme.config.disableAnimations ? 'auto' : 'smooth' });
          }
        }
      }

      // Radios are re-queried fresh on every use because the theme can
      // re-render the product-info section HTML on variant change, which
      // replaces the radio elements and would leave cached references (and
      // their listeners) pointing at detached nodes.
      getAddonRadios() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return [];
        return [...productInfo.querySelectorAll('[data-additional-product-selector] input[type="radio"]')];
      }

      initAddonImageSelect() {
        const radios = this.getAddonRadios();
        console.log('[addon-image] init, radios found:', radios.length, radios.map((r) => r.dataset.addonTitle));
        if (!radios.length) return;

        // Delegated at document level so it keeps working even after the
        // theme swaps out the product-info HTML (which replaces the radio
        // elements themselves) on a variant change.
        if (!this._addonChangeDelegated) {
          this._addonChangeDelegated = true;
          this._handleAddonChange = (event) => {
            if (event.target && event.target.closest && event.target.closest('[data-additional-product-selector]')) {
              this.selectAddonImage();
            }
          };
          document.addEventListener('change', this._handleAddonChange);
        }

        // A main-product variant change (e.g. color swap) re-slides the
        // gallery to that variant's own featured media, which overrides
        // whichever addon-tagged image was showing. The Rubik Variant
        // Images app embed also force-resets the slider to index 0 on
        // every variant change, asynchronously, ~600ms after the swap.
        // Re-apply the addon selection after that settles so it isn't
        // silently lost to that app's own reset.
        this.handleVariantChangedForAddonImage = () => {
          clearTimeout(this._addonVariantChangeTimeout);
          this._addonVariantChangeTimeout = setTimeout(() => this.selectAddonImage(), 800);
        };
        document.addEventListener('variant:changed', this.handleVariantChangedForAddonImage);

        this.selectAddonImage();

        // Rubik's app embed boots AFTER us on initial page load and rebuilds
        // the slider with every slide restored. No variant:changed fires on
        // load, so nothing used to re-apply the filter and the gallery showed
        // every image regardless of the addon selection. Re-run the filter
        // after Rubik settles, and keep a MutationObserver on the gallery so
        // ANY later external re-insert of slides re-triggers it automatically.
        setTimeout(() => this.selectAddonImage(), 1200);
        setTimeout(() => this.selectAddonImage(), 2800);
        this.initAddonFilterObserver();
      }

      initAddonFilterObserver() {
        if (this._addonObserver) return;

        this._addonObserver = new MutationObserver((mutations) => {
          // Ignore mutations we caused ourselves. The _addonApplying flag is
          // reset via setTimeout(0) after selectAddonImage finishes; observer
          // callbacks are microtasks delivered before that macrotask runs, so
          // our own restore/remove churn is reliably seen while the flag is
          // still on.
          if (this._addonApplying) return;

          const mediaAdded = mutations.some((m) =>
            [...m.addedNodes].some(
              (node) =>
                node.nodeType === 1 &&
                (node.classList?.contains('product__media-item') ||
                  node.classList?.contains('product__thumbs-item') ||
                  node.querySelector?.('.product__media-item, .product__thumbs-item'))
            )
          );
          if (!mediaAdded) return;

          console.log('[addon-image] external script re-inserted slides, re-applying filter');
          clearTimeout(this._addonObserverTimeout);
          this._addonObserverTimeout = setTimeout(() => this.selectAddonImage(), 250);
        });

        this._addonObserver.observe(this, { childList: true, subtree: true });
      }

      // Addon selected: keep the slides tagged for this addon; addon
      // deselected ("none"/Sofa Only): keep every regular photo, drop the
      // addon-tagged ones.
      //
      // Matching is contains-based on the addon title so it covers both the
      // plain convention ("Domino Ottoman") and color-prefixed uploads
      // ("Champagne Mist | Domino Ottoman" - note some templates strip the
      // color prefix before it reaches data-alt, which is fine).
      //
      // Cluster-pruning exists for ONE pathology: the same addon image
      // re-uploaded once per color (nothing ties the copies to a variant,
      // so every copy stays live on every color and we must pick the one
      // nearest the current variant). That case looks like MANY single-slide
      // clusters. When clusters have real size (several distinct addon shots
      // for the current color, possibly split by sofa shots between them),
      // pruning would wrongly hide real photos - so we keep everything.
      //
      // This rebuilds the Swiper wrapper's children instead of toggling a
      // "hidden" (display:none) class. Swiper computes its slide grid only
      // from slides that take up layout space, so once display:none leaves
      // just one sized slide, it decides the track is "locked" (nothing left
      // to scroll to) and silently ignores slideTo/index navigation — the
      // gallery goes blank and stays on whatever was active before. Actually
      // removing the non-matching slides keeps Swiper's slide count in sync
      // with what's visible, so index 0 is always a valid, real slide.
      selectAddonImage() {
        const addonRadios = this.getAddonRadios();
        if (!addonRadios.length) {
          console.log('[addon-image] selectAddonImage: no addonRadios, bailing');
          return;
        }
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) {
          console.log('[addon-image] selectAddonImage: no sliderInstance yet, bailing');
          return;
        }
        const slider = this.sliderInstance.slider;
        if (slider.destroyed) return;

        // Never restructure the gallery while the person is mid-swipe or the
        // slider is animating a transition: yanking slides (and the final
        // slideTo) in that window is exactly what made the gallery snap back
        // to the first image during a swipe. Defer and retry shortly after.
        if (slider.animating || (slider.touchEventsData && slider.touchEventsData.isTouched)) {
          clearTimeout(this._addonBusyRetryTimeout);
          this._addonBusyRetryTimeout = setTimeout(() => this.selectAddonImage(), 400);
          return;
        }

        try {
          this._addonApplying = true;
          this.ensureDeferredLoaded();
          this.beginAddonFilterSettle();

          // Remember which media the person is currently looking at, so a
          // re-run of this filter (Rubik re-inserts, load-time timers,
          // variant:changed re-application) puts them back on the SAME
          // image afterwards instead of always resetting to the first one.
          const prevActiveSlide = slider.slides && slider.slides[slider.activeIndex];
          const prevActiveMediaId = prevActiveSlide
            ? prevActiveSlide.dataset.mediaId || prevActiveSlide.dataset.target || null
            : null;

          const allAddonTitles = addonRadios
            .map((radio) => (radio.dataset.addonTitle || '').trim().toLowerCase())
            .filter(Boolean);

          const checkedRadio = addonRadios.find((radio) => radio.checked);
          const selectedTitle = (checkedRadio?.dataset.addonTitle || '').trim().toLowerCase() || null;

          console.log('[addon-image] selectedTitle:', JSON.stringify(selectedTitle), '| allAddonTitles:', allAddonTitles);

          const altOf = (el) => (el.dataset.alt || el.dataset.mediaAlt || '').trim().toLowerCase();
          const positionOf = (el) => Number(el.dataset.mediaIndex ?? el.dataset.mediaPosition) || 0;
          const mediaIdOf = (el) => el.dataset.mediaId || el.dataset.target || null;
          const altHasAddon = (alt) => allAddonTitles.some((t) => t && alt.indexOf(t) !== -1);

          const productInfo = this.closest('product-info');
          const variantOptionValues = (productInfo?.currentVariant?.options || [])
            .map((o) => String(o).trim().toLowerCase())
            .filter(Boolean);
          const altMatchesCurrentVariant = (alt) =>
            !variantOptionValues.length || variantOptionValues.some((opt) => alt.indexOf(opt) !== -1);

          // Restore whatever WE previously removed from a wrapper (leave
          // Rubik's own removals alone - those slides simply aren't there),
          // then re-sort every child back into its original order using a
          // stable order key stamped onto each slide the first time we ever
          // see it. A captured `nextSibling` reference isn't reliable here:
          // once a slide's recorded "next sibling" has ALSO been removed (any
          // second toggle of the addon on/off), reinserting before it falls
          // back to appending at the end, silently reshuffling order - and
          // since main and thumb are restored independently, they can each
          // reshuffle differently and fall out of sync with each other.
          const restore = (wrapper, removedListKey, orderCounterKey) => {
            const removedList = (this[removedListKey] = this[removedListKey] || []);
            removedList.forEach(({ node }) => wrapper.appendChild(node));
            removedList.length = 0;

            if (this[orderCounterKey] === undefined) this[orderCounterKey] = 0;
            const children = Array.from(wrapper.children);
            children.forEach((el) => {
              if (el.dataset.addonOrder === undefined) {
                el.dataset.addonOrder = this[orderCounterKey]++;
              }
            });
            children
              .slice()
              .sort((a, b) => Number(a.dataset.addonOrder) - Number(b.dataset.addonOrder))
              .forEach((el) => wrapper.appendChild(el));
          };

          restore(slider.wrapperEl, '_addonRemovedMainSlides', '_addonMainOrderCounter');
          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
            restore(this.thumbsInstance.slider.wrapperEl, '_addonRemovedThumbSlides', '_addonThumbOrderCounter');
          }

          // Decide which media ids to keep, using ONLY the main slider's own
          // slides as the source of truth. The main and thumbnail templates
          // number "position" differently (main: a 0-based running index;
          // thumbnail: a 1-based one, each counted across a separately split
          // initial/deferred loop) - clustering each side independently could
          // therefore pick two DIFFERENT clusters that happen to have the
          // same size, leaving the highlighted thumbnail out of sync with
          // the main image. Deciding once by media id and applying that same
          // decision to both wrappers keeps them identical by construction.
          const mainLiveSlides = Array.from(slider.wrapperEl.children).filter((el) => !el.classList.contains('swiper-slide-duplicate'));

          let keptMediaIds = null;
          if (selectedTitle) {
            const candidates = mainLiveSlides.filter((el) => {
              const alt = altOf(el);
              if (alt.indexOf(selectedTitle) === -1) return false;
              // Color-prefixed alts (color survived into data-alt): keep
              // only the current variant's copies; dimension images carry
              // no color and pass for all.
              if (alt !== selectedTitle && alt.indexOf('dimension') === -1) {
                return altMatchesCurrentVariant(alt);
              }
              return true;
            });

            let keepSlides = candidates;

            if (candidates.length > 1) {
              // Resolve the reference position in the SAME numbering space
              // as the slides themselves: find the current variant's
              // featured-media slide among the live slides and use its
              // data index. The raw featured_media.position counts across
              // ALL product media (1-based), while data-media-index is a
              // render-order index that restarts for deferred batches -
              // comparing across those two spaces picks garbage.
              let referencePosition = null;
              const featuredId = productInfo?.currentVariant?.featured_media?.id;
              if (featuredId != null) {
                const refSlide = mainLiveSlides.find((el) => String(mediaIdOf(el)) === String(featuredId));
                if (refSlide) referencePosition = positionOf(refSlide);
              }

              const positions = candidates.map(positionOf).sort((a, b) => a - b);

              const clusters = [[positions[0]]];
              for (let i = 1; i < positions.length; i++) {
                const gap = positions[i] - positions[i - 1];
                if (gap > 1) {
                  clusters.push([positions[i]]);
                } else {
                  clusters[clusters.length - 1].push(positions[i]);
                }
              }

              // Per-color duplicate pathology = MANY clusters, each a single
              // slide (one copy of the same image per color, spaced apart by
              // each color's own photo block). Only then pick the nearest
              // cluster. Any other shape means these are real, distinct
              // addon photos for the current selection - keep them all.
              const allSingletons = clusters.length > 2 && clusters.every((c) => c.length === 1);

              if (allSingletons && referencePosition !== null) {
                let bestCluster = clusters[0];
                let bestDist = Infinity;
                clusters.forEach((cluster) => {
                  const dist = Math.min(...cluster.map((p) => Math.abs(p - referencePosition)));
                  if (dist < bestDist) {
                    bestDist = dist;
                    bestCluster = cluster;
                  }
                });

                console.log('[addon-image] candidate positions:', positions, '| clusters:', clusters, '| singleton-dupe mode, chosen:', bestCluster, '| ref:', referencePosition);

                const keepPositions = new Set(bestCluster);
                keepSlides = candidates.filter((el) => keepPositions.has(positionOf(el)));
              } else {
                console.log('[addon-image] candidate positions:', positions, '| clusters:', clusters, '| keeping ALL candidates (ref:', referencePosition, ')');
              }
            }

            console.log('[addon-image] candidates:', candidates.length, '-> kept', keepSlides.length, 'at positions', keepSlides.map(positionOf));
            keptMediaIds = new Set(keepSlides.map(mediaIdOf));
          }

          const applyToWrapper = (wrapper, removedListKey, label) => {
            const removedList = (this[removedListKey] = this[removedListKey] || []);
            const liveSlides = Array.from(wrapper.children).filter((el) => !el.classList.contains('swiper-slide-duplicate'));

            let visibleCount = 0;
            liveSlides.forEach((el) => {
              const alt = altOf(el);
              let shouldKeep;
              if (selectedTitle) {
                shouldKeep = keptMediaIds.has(mediaIdOf(el));
              } else {
                // Sofa Only: drop anything tagged for ANY addon (any
                // convention), keep the rest.
                shouldKeep = !altHasAddon(alt);
              }
              if (shouldKeep) {
                visibleCount++;
              } else {
                removedList.push({ node: el, nextSibling: el.nextSibling });
                wrapper.removeChild(el);
              }
            });
            console.log(`[addon-image] ${label} slides: live`, liveSlides.length, '-> visible', visibleCount);
          };

          applyToWrapper(slider.wrapperEl, '_addonRemovedMainSlides', 'main');
          slider.update();

          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
            const thumbsSlider = this.thumbsInstance.slider;
            applyToWrapper(thumbsSlider.wrapperEl, '_addonRemovedThumbSlides', 'thumb');
            thumbsSlider.update();
          }

          // Go back to the media the person was on if it survived the
          // filter; only land on the first image when it didn't (which is
          // the deliberate-selection case: the previous image belonged to
          // the other addon state and was just removed).
          let returnIndex = 0;
          if (prevActiveMediaId != null) {
            const slidesNow = Array.from(slider.slides || []);
            const backIndex = slidesNow.findIndex(
              (el) => String(el.dataset.mediaId || el.dataset.target || '') === String(prevActiveMediaId)
            );
            if (backIndex >= 0) returnIndex = backIndex;
          }
          slider.slideTo(returnIndex, 0, true);
          this.syncThumbsPosition();

          const activeMainSlide = slider.slides[slider.activeIndex];
          const thumbsSliderForLog = typeof this.thumbsInstance === 'object' ? this.thumbsInstance.slider : null;
          const thumbSlidesArr = thumbsSliderForLog ? Array.prototype.slice.call(thumbsSliderForLog.slides) : [];
          const activeThumbIndex = thumbSlidesArr.findIndex((s) => s.classList.contains('swiper-slide-thumb-active'));
          const activeThumbSlide = activeThumbIndex >= 0 ? thumbSlidesArr[activeThumbIndex] : null;
          console.log('[addon-image] after slideTo: slides.length', slider.slides.length, 'activeIndex', slider.activeIndex, 'realIndex', slider.realIndex, 'isLocked', slider.isLocked, 'returnIndex', returnIndex, 'prevActiveMediaId', prevActiveMediaId);
          console.log(
            '[addon-image] active main mediaId:', activeMainSlide && mediaIdOf(activeMainSlide),
            '| active thumb mediaId:', activeThumbSlide && mediaIdOf(activeThumbSlide),
            '| SAME MEDIA?', activeMainSlide && activeThumbSlide && mediaIdOf(activeMainSlide) === mediaIdOf(activeThumbSlide),
            '| thumbsActiveIndex', thumbsSliderForLog?.activeIndex,
            '| thumbsRealIndex', thumbsSliderForLog?.realIndex,
            '| thumb-active-class index', activeThumbIndex
          );
          console.log(
            '[addon-image] main order (current DOM):',
            Array.from(slider.wrapperEl.children).filter((el) => !el.classList.contains('swiper-slide-duplicate')).map(mediaIdOf),
            '| thumb order (current DOM):',
            thumbsSliderForLog ? Array.from(thumbsSliderForLog.wrapperEl.children).filter((el) => !el.classList.contains('swiper-slide-duplicate')).map(mediaIdOf) : null
          );
        } catch (error) {
          console.error('[addon-image] selectAddonImage failed:', error);
        } finally {
          this.endAddonFilterSettle();
          // Reset asynchronously so the MutationObserver still sees the flag
          // when it processes the mutations this run just made.
          setTimeout(() => {
            this._addonApplying = false;
          }, 0);
        }
      }

      beginAddonFilterSettle() {
        this.classList.add('is-addon-filtering');
        clearTimeout(this._addonFilterSettleTimeout);
        this._addonFilterSettleTimeout = setTimeout(() => this.endAddonFilterSettle(), 2000);
      }

      endAddonFilterSettle() {
        clearTimeout(this._addonFilterSettleTimeout);
        this.classList.remove('is-addon-filtering');
      }
    }
  );
}
